import { useState } from 'react';

import { TableCell, TableRow, useTheme } from '@mui/material';

import { Permission } from '@/entities/permissions';
import DateCell from '@/pages/Markets/MarketsComponents/shared/DateCell';
import ExpandCell from '@/pages/Markets/MarketsComponents/shared/ExpandCell';
import { getRowBackgroundColor } from '@/pages/Markets/MarketsComponents/shared/tableHelpers';
import { dataRowSx } from '@/pages/Markets/MarketsComponents/shared/tableStyles';
import ValueCell from '@/pages/Markets/MarketsComponents/shared/ValueCell';
import usePermission from '@/shared/hooks/usePermission';
import { formatNumberWithPrecision } from '@/utils/formatNumberWithPrecision';
import { getDiffColor } from '@/utils/getDiffColor';

import CollapsedRow from './CollapsedRow';

import type { MultiGroupTableRowType, SymbolPropertyConfig } from '../../types';

type Props = {
  row: MultiGroupTableRowType;
  index: number;
  columns: SymbolPropertyConfig[];
  isLoading?: boolean;
};

const MultiGroupTableRow = ({ row, index, columns, isLoading = false }: Props) => {
  const theme = useTheme();
  const { hasPermission } = usePermission();
  const isRestricted = !hasPermission(Permission.ViewMarketScreens);
  const { shortTitle, date, last, delta, bValues } = row;

  const hasAnyValue = Boolean(last) || Boolean(bValues && Object.keys(bValues).length > 0);
  const restrictedData = !hasAnyValue || isRestricted;
  const backgroundColor = getRowBackgroundColor(restrictedData, index, theme);

  const [open, setOpen] = useState(false);

  const lastDisplayValue = last ? formatNumberWithPrecision(last) : '';
  const deltaDisplayValue = delta ? formatNumberWithPrecision(delta) : '';

  const colSpan = 5 + columns.length;

  return (
    <>
      <TableRow
        key={shortTitle}
        className='markets-table-row'
        sx={{ ...dataRowSx, backgroundColor }}
      >
        <ExpandCell
          open={open}
          disabled={restrictedData}
          onToggle={() => {
            setOpen(!open);
          }}
        />
        <TableCell
          component='th'
          scope='row'
        >
          {shortTitle}
        </TableCell>
        <DateCell
          date={date}
          isLoading={isLoading}
        />
        <ValueCell
          isLoading={isLoading}
          hasValue={Boolean(last)}
          displayValue={lastDisplayValue}
          restricted={restrictedData}
        />
        <ValueCell
          isLoading={isLoading}
          hasValue={Boolean(delta)}
          displayValue={deltaDisplayValue}
          restricted={isRestricted}
          sx={{ color: getDiffColor(Number(delta)) }}
        />
        {columns.map(column => {
          const raw = bValues?.[column.name];
          const display = raw ? formatNumberWithPrecision(raw) : '';
          return (
            <ValueCell
              key={column.name}
              isLoading={isLoading}
              hasValue={Boolean(raw)}
              displayValue={display}
              restricted={restrictedData}
            />
          );
        })}
      </TableRow>
      <TableRow sx={{ backgroundColor }}>
        <CollapsedRow
          colSpan={colSpan}
          open={open}
          row={row}
        />
      </TableRow>
    </>
  );
};

export default MultiGroupTableRow;
